// Phase 2.11 Tier 3 — bulk-update mobile UI controller.
//
// Behaviors:
//   - Master checkbox toggles all row checkboxes.
//   - Field <select> swaps which input wrapper is visible (date /
//     number / email / tel / text) and copies the matching variant's
//     value into a hidden `value` field that POSTs cleanly.
//   - Apply button stays disabled until at least one row is selected.
//   - The footer shows live `N selected` so the field tech sees what
//     they're about to commit.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "masterCheckbox", "rowCheckbox", "counter",
    "fieldSelect", "fieldWrap", "valueInput", "hiddenValue",
    "applyButton", "applyCounter"
  ]

  connect() {
    this._syncHiddenValue()
    this.updateCount()
  }

  toggleAll(event) {
    const next = !!event.target.checked
    this.rowCheckboxTargets.forEach((cb) => { cb.checked = next })
    this.updateCount()
  }

  fieldChanged() {
    const next = this.fieldSelectTarget.value
    this.fieldWrapTargets.forEach((w) => {
      w.classList.toggle("hidden", w.dataset.field !== next)
    })
    // When the field changes, focus the now-visible input.
    const visible = this._activeInput()
    visible?.focus()
    this._syncHiddenValue()
  }

  // Wire `input`/`change` from any value input into the hidden POST
  // field. Stimulus' built-in `keyup`/`change` events on `valueInput`
  // would each need a shadow handler — instead, rebuild the hidden
  // value at form-submit time and on every keystroke via delegation.
  inputChanged() {
    this._syncHiddenValue()
  }

  updateCount() {
    const total = this.rowCheckboxTargets.length
    const selected = this.rowCheckboxTargets.filter((cb) => cb.checked).length

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${total} shown · ${selected} selected`
    }
    if (this.hasApplyCounterTarget) {
      this.applyCounterTarget.textContent = `${selected} selected`
    }
    if (this.hasMasterCheckboxTarget) {
      this.masterCheckboxTarget.checked = selected === total && total > 0
      this.masterCheckboxTarget.indeterminate = selected > 0 && selected < total
    }
    if (this.hasApplyButtonTarget) {
      this.applyButtonTarget.disabled = selected === 0
    }
    this._syncHiddenValue()
  }

  _activeInput() {
    const field = this.fieldSelectTarget?.value
    if (!field) return null
    return this.valueInputTargets.find((el) => el.dataset.field === field)
  }

  _syncHiddenValue() {
    const active = this._activeInput()
    if (this.hasHiddenValueTarget) {
      this.hiddenValueTarget.value = active?.value || ""
    }
  }

  // Catch every input keystroke on the form (event-delegation pattern)
  // so the hidden `value` stays in lockstep with whichever variant
  // input the user typed in.
  formInput(event) {
    if (event.target?.matches?.("[data-asset-bulk-update-target='valueInput']")) {
      this._syncHiddenValue()
    }
  }
}
